import {ReactNode, useEffect} from 'react';
import {Outlet} from 'react-router-dom';
import {ConfirmationHost} from '@/components/app/confirmation';
import {cn} from '@/lib/utils';
import {InfoSection} from './app/components/InfoSection';
import {useBrowserSupported} from './app/hooks/useBrowserSupported';
import {useDevice} from './app/hooks/useDevice';
import {useAppSelector} from './app/hooks/useAppSelector';
import {getConfigValue} from './app/utils/configUtils';
import {selectCurrentToken} from './features/auth/authSlice';
import {SignalRHub} from './app/vendors/signalRHubFactory';
import {biotwinHub} from './features/core/hubs/biotwinHub';
import {subscriptionHub} from './features/core/hubs/subscriptionHub';
import {useAnalytics} from './features/core/hooks/useAnalytics';
import {useNetworkStatus} from './features/core/hooks/useNetworkStatus';
import {toastWarning} from './app/toast';
export const App = () => {
  const browserSupported = useBrowserSupported();
  const {isMobile} = useDevice();
  const {isOffline, isSlow} = useNetworkStatus();
  useAnalytics();
  useEffect(() => {
    if (isOffline) {
      toastWarning('Please check your internet connection.');
    }
  }, [isOffline]);
  useEffect(() => {
    if (isSlow) {
      toastWarning('Slow internet connection is detected.');
    }
  }, [isSlow]);
  if (!browserSupported) {
    return (
      <InfoSection
        className={cn({
          'h-full': !isMobile,
        })}
        icon={'exclamation-circle'}
        title={'Unsupported Browser Version'}
        subtitle={''}
        content={
          <>
            <p>
              You are using an outdated version of this browser that does not meet the minimum requirements to view
              Biotandem properly.
            </p>
            <p>Please upgrade your browser.</p>
          </>
        }
      />
    );
  }
  return (
    <div
      className={cn({
        'h-full': !isMobile,
      })}
    >
      <HubProvider disabled hub={biotwinHub}>
        <HubProvider disabled hub={subscriptionHub}>
          <Outlet />
          <ConfirmationHost />
        </HubProvider>
      </HubProvider>
    </div>
  );
};
interface HubProviderProps {
  disabled?: boolean;
  children: ReactNode;
  hub: SignalRHub;
}
function HubProvider({disabled, children, hub}: HubProviderProps) {
  const token = useAppSelector(selectCurrentToken);
  const signalRUrl = getSignalRUrl();
  return (
    <hub.Provider
      accessTokenFactory={() => token ?? ''}
      connectEnabled={!disabled && !!token}
      dependencies={[token]}
      headers={{
        'ngrok-skip-browser-warning': 'true',
      }}
      withCredentials={false}
      url={`${signalRUrl}/${hub.name}`}
    >
      {children}
    </hub.Provider>
  );
  function getSignalRUrl() {
    return import.meta.env.VITE_NGROK_ENABLED === 'true'
      ? import.meta.env.VITE_NGROK_URL
      : new URL(getConfigValue('apiUrl')).origin;
  }
}
